import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Gift } from "lucide-react";
import { useCart } from "@/store/cart";
import { useAds } from "@/store/ads";

/**
 * Buy 2 Get 1 Free upsell shown on the FIRST add-to-cart in a session,
 * right before the cart drawer slides out. Accepting bumps the just-added
 * product to qty=3 and auto-applies the BOGO promo code (33% off, min $38).
 */
export default function BogoUpsell() {
  const { pendingUpsell, resolveUpsell } = useCart();
  const { setPromoCode } = useAds();

  const product = pendingUpsell?.product;
  const qty = pendingUpsell?.qty ?? 1;
  const price = product?.price ?? 0;
  const bundleQty = qty + 2;                          // qty after accepting (3 for a 1-add)
  const originalTotal = price * bundleQty;            // e.g. 3 × $12.99 = $38.97
  const bogoTotal = originalTotal * (1 - 0.33);       // 33% off — matches BOGO coupon
  const savings = originalTotal - bogoTotal;          // ≈ one freshener free

  const accept = () => {
    setPromoCode("BOGO");
    resolveUpsell(true);
  };
  const decline = () => resolveUpsell(false);

  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={decline}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm"
            data-testid="bogo-overlay"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            role="dialog"
            aria-modal="true"
            aria-label="Buy 2 Get 1 Free offer"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-[92vw] max-w-md rounded-2xl border border-[#FF3D8B]/30 bg-[#0a0a0a] shadow-[0_0_80px_rgba(255,61,139,0.35)] p-8 text-white"
            data-testid="bogo-modal"
          >
            <button
              onClick={decline}
              aria-label="Close offer"
              data-testid="bogo-close-btn"
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF3D8B]/40 bg-[#FF3D8B]/10 text-[#FF3D8B] text-[10px] uppercase tracking-widest">
              <Sparkles size={12} /> Wait — one-time offer
            </div>

            <h2 className="mt-5 font-display text-4xl leading-tight text-white">
              Buy 2, <span className="text-neon-pink">get 1 free.</span>
            </h2>

            <p className="mt-3 text-white/70 text-sm leading-relaxed">
              Add 2 more <span className="text-white font-semibold">{product.name}</span> to your bag and
              we'll knock <span className="text-[#FF3D8B] font-semibold">${savings.toFixed(2)} off</span> at checkout — auto-applied.
            </p>

            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-white/60">
                <span>{bundleQty} × {product.name}</span>
                <span className="line-through">${originalTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white font-semibold text-base">
                <span>Your price</span>
                <span data-testid="bogo-price">${bogoTotal.toFixed(2)}</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[#A855F7] pt-1">
                Promo <span className="text-white/80">BOGO</span> applied automatically
              </div>
            </div>

            <div className="mt-7 flex flex-col gap-2.5">
              <button
                onClick={accept}
                data-testid="bogo-accept-btn"
                className="pulse-pink inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
              >
                <Gift size={16} /> Yes — add 2 more, save ${savings.toFixed(2)}
              </button>
              <button
                onClick={decline}
                data-testid="bogo-decline-btn"
                className="w-full py-2.5 rounded-full text-white/50 hover:text-white text-xs uppercase tracking-widest transition-colors"
              >
                No thanks, just the one
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
